import React from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { useApiClient } from '../api/client';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../toast/ToastProvider';

// mapbox-gl is loaded via CDN in public/index.html
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const mapboxgl: any;

type Suggestion = {
  id: string;
  name: string;
  description: string;
};

type SelectedLocation = {
  lng: number;
  lat: number;
  name: string;
  addressLine?: string;
  rawFeatureJson?: string;
};

const stripAustria = (value?: string | null) => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;

  const stripped = trimmed.replace(/\s*,\s*(Austria|Österreich|Oesterreich)\s*$/i, '').trim();
  return stripped || undefined;
};

const withPublicName = (publicName: string, context?: string | null) => {
  const trimmedContext = context?.trim();
  if (!trimmedContext) return undefined;
  if (!publicName) return trimmedContext;
  return trimmedContext.toLocaleLowerCase().startsWith(publicName.toLocaleLowerCase())
    ? trimmedContext
    : `${publicName}, ${trimmedContext}`;
};

const mapboxAddressLine = (feature: any, suggestion: Suggestion, publicName: string) => {
  const properties = feature?.properties ?? {};
  const contextualAddress =
    properties.full_address ||
    properties.place_name ||
    feature?.place_name ||
    withPublicName(publicName, properties.place_formatted) ||
    suggestion.description;

  return stripAustria(String(contextualAddress || ''));
};

export const LocationCreatePage: React.FC = () => {
  const { t, language } = useI18n();
  const { request } = useApiClient();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState<Suggestion[]>([]);
  const [showResults, setShowResults] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [selectedLocation, setSelectedLocation] = React.useState<SelectedLocation | null>(null);
  const [privateName, setPrivateName] = React.useState('');
  const [publicName, setPublicName] = React.useState('');
  const [isPrivate, setIsPrivate] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const mapContainerRef = React.useRef<HTMLDivElement | null>(null);
  const mapRef = React.useRef<any | null>(null);
  const markerRef = React.useRef<any | null>(null);

  const token = (process.env.REACT_APP_MAPBOX_TOKEN || '').trim();

  const sessionTokenRef = React.useRef<string>('');
  if (!sessionTokenRef.current) {
    try {
      // @ts-ignore
      sessionTokenRef.current = (crypto && typeof crypto.randomUUID === 'function') ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    } catch {
      sessionTokenRef.current = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    }
  }

  // Initialize map
  React.useEffect(() => {
    if (!token) return;
    if (!mapContainerRef.current) return;
    if (mapRef.current) return;

    mapboxgl.accessToken = token;
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [11.576124, 48.137154],
      zoom: 11,
    });
    mapRef.current = map;

    map.on('click', (e: any) => {
      const lng = Number(e.lngLat.lng);
      const lat = Number(e.lngLat.lat);
      setSelectedLocation({
        lng,
        lat,
        name: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
      });
      setPublicName(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [token]);

  // Update marker when location changes
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedLocation) return;

    if (!markerRef.current) {
      markerRef.current = new mapboxgl.Marker({ color: '#ef4444' });
    }
    markerRef.current.setLngLat([selectedLocation.lng, selectedLocation.lat]).addTo(map);
    map.flyTo({ center: [selectedLocation.lng, selectedLocation.lat], zoom: 14 });
  }, [selectedLocation]);

  // Search for locations
  React.useEffect(() => {
    if (!showResults || !query || query.length < 3) {
      setResults([]);
      return;
    }
    if (!token) return;

    const controller = new AbortController();
    const run = async () => {
      setLoading(true);
      try {
        const url = new URL('https://api.mapbox.com/search/searchbox/v1/suggest');
        url.searchParams.set('q', query);
        url.searchParams.set('access_token', token);
        url.searchParams.set('language', language);
        url.searchParams.set('limit', '8');
        url.searchParams.set('types', 'poi,address,place,locality,neighborhood,region,street');
        url.searchParams.set('session_token', sessionTokenRef.current);
        const res = await fetch(url.toString(), { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const suggestions: Suggestion[] = (json.suggestions || []).map((s: any) => ({
          id: String(s.mapbox_id),
          name: String(s.name || s.full_address || s.place_formatted || s.feature_name || ''),
          description: String(s.place_formatted || s.full_address || ''),
        }));
        setResults(suggestions);
      } catch (e: any) {
        if (e.name !== 'AbortError') console.error(e);
      } finally {
        setLoading(false);
      }
    };

    const handle = setTimeout(run, 250);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [query, showResults, token, language]);

  const resolveSuggestion = async (s: Suggestion) => {
    if (!token) return;
    const url = new URL(`https://api.mapbox.com/search/searchbox/v1/retrieve/${encodeURIComponent(s.id)}`);
    url.searchParams.set('access_token', token);
    url.searchParams.set('session_token', sessionTokenRef.current);
    const res = await fetch(url.toString());
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    const f = json.feature || (Array.isArray(json.features) ? json.features[0] : null);
    const coords = f?.geometry?.coordinates;
    const name: string = f?.properties?.name || f?.name || s.name;
    if (!Array.isArray(coords) || coords.length < 2) return;
    const addressLine = mapboxAddressLine(f, s, name);
    
    setSelectedLocation({
      lng: Number(coords[0]),
      lat: Number(coords[1]),
      name,
      addressLine,
      rawFeatureJson: JSON.stringify(json),
    });
    setPublicName(name);
    setQuery(addressLine || name);
    setResults([]);
    setShowResults(false);
  };

  const handleSave = async () => {
    if (!selectedLocation) return;
    setSaving(true);
    try {
      const payload = {
        publicName: publicName.trim(),
        addressLine: selectedLocation.addressLine || null,
        longitude: selectedLocation.lng,
        latitude: selectedLocation.lat,
        rawFeatureJson: selectedLocation.rawFeatureJson || null,
        isPrivate,
        privateName: isPrivate && privateName.trim() ? privateName.trim() : null,
      };
      
      await request('/locations', { method: 'POST', body: JSON.stringify(payload) });
      showSuccess(t('locations.createSuccess'));
      navigate('/locations');
    } catch (e: any) {
      showError(`${t('common.error')}: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-4">
        <button
          onClick={() => navigate('/locations')}
          className="text-sm text-gray-600 hover:text-gray-900"
        >
          ← {t('action.back')}
        </button>
      </div>

      <h1 className="text-2xl font-extrabold mb-4">{t('locations.createTitle')}</h1>

      {!token && (
        <div className="mb-4 rounded bg-yellow-50 p-3 text-sm text-yellow-900 border border-yellow-200">
          Missing REACT_APP_MAPBOX_TOKEN env var.
        </div>
      )}

      <div className="grid gap-6 max-w-4xl">
        <div>
          <label className="block text-sm font-semibold mb-2">{t('locations.searchLocation')}</label>
          <input
            type="text"
            value={query}
            onFocus={() => setShowResults(true)}
            onChange={(e) => { setQuery(e.target.value); setShowResults(true); }}
            placeholder={t('locations.searchPlaceholder')}
            className="w-full rounded border px-3 py-2"
          />
          {showResults && results.length > 0 && (
            <ul className="mt-2 divide-y rounded border bg-white">
              {results.map((r) => (
                <li
                  key={r.id}
                  className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                  onClick={() => resolveSuggestion(r)}
                >
                  <div className="font-medium">{r.name}</div>
                  {r.description && <div className="text-gray-600 text-xs">{r.description}</div>}
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-gray-500 mt-1">
            {t('locations.searchOrClickMap')}
          </p>
        </div>

        {token && (
          <div className="rounded border overflow-hidden">
            <div ref={mapContainerRef} style={{ width: '100%', height: 400 }} />
          </div>
        )}

        {selectedLocation && (
          <div className="rounded border bg-white p-6">
            <h2 className="text-lg font-semibold mb-4">{t('locations.locationDetails')}</h2>
            
            <div className="grid gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1">
                  {t('locations.isPrivate')}
                </label>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={!isPrivate}
                      onChange={() => setIsPrivate(false)}
                    />
                    <span className="text-sm">{t('locations.public')}</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={isPrivate}
                      onChange={() => setIsPrivate(true)}
                    />
                    <span className="text-sm">{t('locations.private')}</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('locations.privateDescription')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">
                  {t('locations.publicName')}
                </label>
                <input
                  type="text"
                  value={publicName}
                  onChange={(e) => setPublicName(e.target.value)}
                  className="w-full rounded border px-3 py-2"
                  placeholder={t('locations.publicNamePlaceholder')}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('locations.publicNameDescription')}
                </p>
              </div>

              {isPrivate && (
                <div>
                  <label className="block text-sm font-semibold mb-1">
                    {t('locations.privateName')}
                  </label>
                  <input
                    type="text"
                    value={privateName}
                    onChange={(e) => setPrivateName(e.target.value)}
                    className="w-full rounded border px-3 py-2"
                    placeholder={t('locations.privateNamePlaceholder')}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('locations.privateNameDescription')}
                  </p>
                </div>
              )}

              <div className="border-t pt-4">
                <div className="text-sm font-semibold mb-1">{t('locations.coordinates')}</div>
                <div className="text-sm text-gray-600">
                  {t('locations.longitude')}: {selectedLocation.lng.toFixed(6)}
                </div>
                <div className="text-sm text-gray-600">
                  {t('locations.latitude')}: {selectedLocation.lat.toFixed(6)}
                </div>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button
                  onClick={handleSave}
                  disabled={saving || !publicName.trim()}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-extrabold text-black hover:brightness-95 disabled:opacity-50"
                >
                  {saving ? t('common.saving') : t('locations.create')}
                </button>
                <button
                  onClick={() => navigate('/locations')}
                  disabled={saving}
                  className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                >
                  {t('action.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationCreatePage;
